import { styled } from "@linaria/react"
import { Link, useNavigate } from "@tanstack/react-router"
import { observer } from "mobx-react-lite"
import { useState } from "react"

import { Centered } from "@/components/Centered/Centered.ts"
import { Horizontal, Spring } from "@/components/Flex.ts"
import { SecondaryTextSpan } from "@/components/SecondaryTextSpan/SecondaryTextSpan.ts"
import { Version } from "@/components/Version/Version.tsx"
import { useModel } from "@/hooks/useModel.ts"
import { Navigator } from "@/routes/Navigator.ts"
import { useCurrentOrgId } from "@/routes/projects/projectHooks.ts"
import { TaskViewId } from "@/routes/tasks/TaskViewId.ts"
import { CreatePrDialog } from "@/routes/tasks/TaskViewScene/CreatePrDialog.tsx"
import { TaskMoreMenu } from "@/routes/tasks/TaskViewScene/TaskMoreMenu.tsx"
import { TaskVersionsModel } from "@/routes/tasks/TaskViewScene/TaskVersionsModel.ts"
import * as textStyles from "@/styles/textStyles.ts"

import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"


export const TaskVersionsTab = observer((props: {
  model: TaskViewModel
}) => {
  const model = useModel(() => {
    return new TaskVersionsModel(props.model, () => {
      props.model.gitHubModel.trackCreatePullRequestClick()
      setPrDialogOpen(true)
    })
  }, [props.model])

  const versions = model.versions.get()

  const navigate = useNavigate()
  const orgId = useCurrentOrgId()

  const [prDialogOpen, setPrDialogOpen] = useState(false)

  const task = props.model.task.get()

  const me = props.model.me.get()

  return (
    <Container gap="36px" padding={versions.length > 0 ? "0 0 8px" : "0"}>
      <Header model={props.model} />

      {versions.length == 0 && (
        <Empty>
          <SecondaryTextSpan>
            You will find recent attempts here once the plan is executed successfully
          </SecondaryTextSpan>
          <Link to="." search={{ view: TaskViewId.Full }}>Back to task</Link>
        </Empty>
      )}

      {task && me && (
        <CreatePrDialog
          isOpen={prDialogOpen}
          onOpenChange={setPrDialogOpen}
          taskModel={props.model}
          taskLink={props.model.taskLink}
          task={task}
          author={me}
          historyItems={props.model.historyItems.get()}
        />
      )}

      {versions.map((version) => (
        <Version
          key={version.number}
          number={version.number}
          commitId={version.commitId}
          isCurrent={version.isCurrent}
          isSuccessful={version.isSuccessful}
          createdAt={version.createdAt}
          createdBy={version.createdBy}
          title={version.title}
          subtitle={version.subtitle}
          isGenerating={version.isGenerating}
          limitedWidth
          borderless
          gitHubVersionInfo={version.gitHubVersionInfo}
          onCheckout={() =>
            props.model.checkout(version.commitId).then(() =>
              navigate(Navigator.task(orgId, props.model.project.get().key, props.model.taskId, { view: TaskViewId.Full }))
            )
          }
          onMarkSuccessful={(commitId, successful) => props.model.markAsSuccessful(commitId, successful)}
          onClick={version.isCurrent
            ? () => navigate(Navigator.task(orgId, props.model.project.get().key, props.model.taskId, { view: TaskViewId.Full }))
            : null
          }
        />
      ))}
    </Container>
  )
})

const Header = (props: { model: TaskViewModel }) => {
  return (
    <HeaderContainer>
      <AllVersionsTitle>
        All versions
      </AllVersionsTitle>
      <Spring />
      <TaskMoreMenu model={props.model} />
    </HeaderContainer>
  )
}

const Container = styled(Centered)`
  container-type: inline-size;
  container-name: task-versions-tab;
`

const HeaderContainer = styled(Horizontal)`
  gap: 8px;
  align-items: center;

  width: 100%;
  white-space: nowrap;

  ${textStyles.smallFontSize};
`

const AllVersionsTitle = styled.span`
  color: var(--secondary-text-color);
  font-weight: 500;
`

const Empty = styled(Centered)`
  gap: 8px;
  max-width: 320px;
  text-align: center;
`